import { useState } from 'react'
import { useRouter } from 'next/router'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import api from 'lib/api'
import { useStore } from 'lib/store'
import { useToast } from 'hooks/useToast'

import { VacancyType } from 'types/vacancy'
import { Apply, applyVacancyInJobs } from '../utils'
import {
  Button,
  IconProfilerGroup,
  ModalAction,
  ModalRecommendation
} from 'components'
import {
  recommendationModalSchema,
  defaultValues
} from 'utils/form/recommendation-modal'
import { handleToAuth } from 'services/user'
import { getMetricName } from 'utils/page'
import { WhatsappButton } from './whatsapp-button'
import { DrawerPreCandidature } from './DrawerPreCandidature'

interface Props {
  id: number
  title: string
  startSolidesEnabled: boolean
  type: VacancyType
  verifyRequiredFields: boolean
  pcdOnly: boolean
  preApplyEnabled?: boolean
  onSuccess: () => void
  disabledListBlocks: boolean
}

type ModalType =
  | 'review-curriculum'
  | 'internal-apply'
  | 'external-apply'
  | 'form-recommendation'
  | 'vacancy-pcd'
  | 'duplicate-acount'
  | 'without-profiler'
  | 'complete-required-fields'
  | 'pre-apply-success'

const alertModals = {
  'internal-apply': {
    icon: 'business_center',
    title: 'Candidatura Interna',
    description:
      'Esta vaga foi planejada apenas para os colaboradores desta empresa. Caso você seja um colaborador, entre em contato com o seu RH.'
  },
  'external-apply': {
    icon: 'business_center',
    title: 'Candidatura Externa',
    description:
      'Você não pode se candidatar a esta vaga, por ser colaborador desta empresa e a vaga trata-se de uma candidatura externa.'
  },
  'vacancy-pcd': {
    icon: 'accessible',
    title: 'Vaga PCD',
    description:
      'Vaga destinada apenas para pessoas PCD (Pessoas com Deficiência). Portanto não é possível se candidatar'
  }
}

export const CandidatureButton = ({
  disabledListBlocks,
  verifyRequiredFields,
  onSuccess,
  ...vacancy
}: Props) => {
  const toast = useToast()
  const router = useRouter()
  const queryClient = useQueryClient()

  const { company, user, updateStoreUser } = useStore(store => store)
  const [modal, setModal] = useState<ModalType>(null)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)
  const [loadingApply, setLoadingApply] = useState<boolean>(false)
  const [applyStatus, setApplyStatus] = useState<ModalType>(null)

  const { data: blocks } = useQuery({
    queryFn: async () => {
      const data = await api<string[]>('/recommendations-block', {
        method: 'GET',
        params: { email: user.email }
      })

      return data.data
    },
    queryKey: ['recommendations-block', user?.email],
    enabled: !disabledListBlocks
  })

  const useFormMethods = useForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(recommendationModalSchema)
  })

  const handleClickCandidatureButton = async () => {
    if (!user) {
      handleToAuth()
      return
    } else if (applyStatus) {
      setModal(applyStatus)
      return
    }

    try {
      if (vacancy.type !== 'mista') {
        const route = `/applicants/check-employee/${vacancy.id}`
        // eslint-disable-next-line prettier/prettier
        const response = await api<{ isActiveEmployee: boolean }>(
          route,
          {},
          'applicants'
        )
        const isEmployee = response.data.isActiveEmployee

        if (!isEmployee && vacancy.type === 'interna') {
          setModal('internal-apply')
          setApplyStatus('internal-apply')
          return
        } else if (isEmployee && vacancy.type === 'externa') {
          setModal('external-apply')
          setApplyStatus('external-apply')
          return
        }
      }

      if (verifyRequiredFields) {
        setLoadingApply(true)

        const route = '/vacancies/enabledtoapply'
        const params = { id: vacancy.id, email: user.email }
        const response = await api<string[]>(route, { params })

        setLoadingApply(false)

        if (response.data.length) {
          setModal('review-curriculum')
          setApplyStatus('review-curriculum')

          return
        }
      }

      setModal('form-recommendation')
    } catch (err) {
      console.error(err)
      console.log(err?.response?.message)
      toast.error({ title: 'Falha ao realizar a candidatura!' })
    } finally {
      setLoadingApply(false)
    }
  }

  const getApplyData = async (
    goCurriculum = false
  ): Promise<Apply | string> => {
    const source = router.query?.origem as string
    const applicationSource = getMetricName()

    if (goCurriculum) {
      const params = new URLSearchParams()
      params.append('vacancyId', String(vacancy.id))
      params.append('applicationSource', applicationSource)
      source && params.append('source', source)

      return params.toString()
    }

    const apply = {
      vacancy: { id: vacancy.id, title: vacancy.title, source },
      company: { name: company.name, slug: company.slug },
      applicationSource,
      recommendations: null
    }

    const formData = useFormMethods.getValues()

    if (formData.isConfirmed === 'sim') {
      const isValid = await useFormMethods.trigger()

      if (!isValid) {
        return null
      }

      if (blocks && blocks.includes(formData.email)) {
        useFormMethods.setError('email', {
          type: 'email-block',
          message: 'E-mail não disponível, tente contactar outra pessoa.'
        })

        return null
      }

      apply.recommendations = {
        name: formData.name,
        email: formData.email
      }
    }

    return apply
  }

  const handleReviewCurriculum = async (preApply = false) => {
    const paramsApply = (await getApplyData(true)) as string
    const params = preApply ? `${paramsApply}&pre-apply=true` : paramsApply
    applyVacancyInJobs(params)
  }

  const handlePreApplySuccess = () => setModal('pre-apply-success')

  const handlePreApplyLater = () => {
    setModal(null)
    queryClient.setQueryData(['vacancy-applied', vacancy.id], 'pre-applied')
  }

  const usePreApply = !!vacancy.preApplyEnabled && !vacancy.pcdOnly

  const handleApplyVacancy = async () => {
    const apply = (await getApplyData()) as Apply

    if (apply) {
      const data = {
        ...apply,
        vacancyId: apply.vacancy.id,
        source: apply.vacancy.source
      }

      setLoadingApply(true)
      setModal(null)

      try {
        const route = '/applicants/apply-job'
        await api(route, { method: 'POST', data }, 'applicants')

        queryClient.setQueryData(['vacancy-applied', vacancy.id], 'applied')
        updateStoreUser({ appliedTalentBank: true })

        onSuccess()
      } catch (err) {
        const status = err?.response?.status
        const errMessage = err?.response?.data?.title as string

        const mapperTypeModals = {
          409: 'duplicate-acount',
          401: 'without-profiler'
        }

        let typeModal = mapperTypeModals[status]

        if (status === 400) {
          if (vacancy.pcdOnly) {
            typeModal = 'vacancy-pcd'
          } else if (errMessage.includes('Cadastro incompleto')) {
            typeModal = 'complete-required-fields'
          }
        }

        if (status === 409) {
          setErrorDetail(errMessage.split(':')[1].trim())
        }

        if (typeModal) {
          setModal(typeModal)
          setApplyStatus(typeModal)
        } else {
          toast.error({ title: errMessage || 'Falha ao concluir candidatura' })
        }
      }

      setLoadingApply(false)
    }
  }

  const handleApplyWithProfiler = async () => {
    const paramsApply = (await getApplyData(true)) as string
    applyVacancyInJobs(paramsApply, true)
  }

  const handleGoToSupport = () => {
    const url =
      'https://ajuda.solides.com.br/hc/pt-br/categories/4411290914189-Sou-candidato-a-'
    window.open(url, '_blank').focus()
  }

  return (
    <>
      <FormProvider {...useFormMethods}>
        <ModalRecommendation
          onClose={() => setModal(null)}
          open={modal === 'form-recommendation'}
          onReviewCurriculum={() => handleReviewCurriculum()}
          onApplyVacancy={handleApplyVacancy}
        />
      </FormProvider>

      <DrawerPreCandidature
        vacancyId={vacancy.id}
        vacancyTitle={vacancy.title}
        open={modal === 'review-curriculum' && usePreApply}
        onClose={() => setModal(null)}
        onSuccess={handlePreApplySuccess}
        onReviewCurriculum={() => handleReviewCurriculum()}
      />

      <ModalAction
        icon="offline_bolt"
        open={modal === 'review-curriculum' && !usePreApply}
        title={
          <>
            Você está a 1 passo
            <br />
            da sua candidatura
          </>
        }
        description="Para concluir sua candidatura você deverá revisar seu currículo e preencher os campos obrigatórios exigidos pela vaga. Deseja revisar seu currículo?"
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{
          action: () => handleReviewCurriculum(),
          label: 'Revisar'
        }}
      />

      <ModalAction
        size="480px"
        icon="order_approve"
        iconVariant="round"
        open={modal === 'pre-apply-success'}
        title="Estamos quase lá!"
        description="A pessoa recrutadora solicitou algumas informações obrigatórias para concluir sua candidatura."
        cancelButton={{
          action: handlePreApplyLater,
          label: 'Preencher depois'
        }}
        confirmButton={{
          action: () => handleReviewCurriculum(true),
          label: 'Preencher agora'
        }}
      />

      <ModalAction
        icon="help"
        open={modal === 'duplicate-acount'}
        title="Não é possível se candidatar a vagas dessa empresa"
        description={`O CPF informado já está cadastrado em outra conta com o e-mail ${errorDetail}. Para continuar, você precisa excluir uma das contas ou entrar em contato com o suporte.`}
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{ action: handleGoToSupport, label: 'Suporte' }}
      />

      <ModalAction
        iconVariant="round"
        title=""
        open={!!alertModals[modal]}
        confirmButton={{ action: () => setModal(null), label: 'Ok, entendi!' }}
        {...alertModals[modal]}
      />

      <ModalAction
        size="400px"
        icon={<IconProfilerGroup />}
        open={modal === 'without-profiler'}
        onClose={() => setModal(null)}
        title="Conclua o Profiler para garantir sua candidatura."
        description="Vimos que você quer se candidatar para a vaga, mas essa etapa é obrigatória. Se você sair agora, sua candidatura não será salva e o recrutador não receberá seu perfil. Vamos completar o Profiler?"
        confirmButton={{
          action: () => handleApplyWithProfiler(),
          label: 'Preencher agora'
        }}
      />

      <ModalAction
        icon="offline_bolt"
        iconVariant="round"
        open={modal === 'complete-required-fields'}
        title="Você está a alguns passos da sua candidatura"
        description="Para concluir sua candidatura você deverá revisar seu currículo e preencher os campos obrigatórios exigidos pela vaga. Deseja revisar seu currículo?"
        cancelButton={{ action: () => setModal(null) }}
        confirmButton={{
          action: () => handleReviewCurriculum(),
          label: 'Revisar'
        }}
      />

      <div className="w-full flex bg-white fixed bottom-0 right-0 left-0 shadow-2xl px-4 xl:px-0">
        <div className="max-w-7xl w-full mx-auto flex flex-col sm:flex-row gap-y-2 gap-x-4 justify-center py-4">
          {vacancy.startSolidesEnabled && <WhatsappButton {...vacancy} />}
          <Button
            colorCompany
            className="py-3 w-full sm:w-auto"
            onClick={handleClickCandidatureButton}
            loading={loadingApply && 'Candidatando...'}
          >
            Quero me candidatar
          </Button>
        </div>
      </div>
    </>
  )
}
